import { PropsWithChildren, useContext } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { StepContext } from '../provider';
import { ButtonBar } from './button-bar';
import { HeaderBar } from './header-bar';

type StepProps = PropsWithChildren & {
  title: string;
  description?: string;
  isEditMode?: boolean;
  primaryButtonDisabled?: boolean;
  primaryButtonLabel?: string;
  onPrimaryButtonClick: () => void;
  trackingIdPrefix?: string;
};

export const Step = ({
  title,
  description,
  primaryButtonDisabled,
  primaryButtonLabel,
  onPrimaryButtonClick,
  children,
  trackingIdPrefix,
}: StepProps) => {
  const { t } = useTranslation('phone');

  const { onBackClick, onCancelClick, onClose, isEditMode, isChangingStep, omitBackActions } = useContext(StepContext);

  const isChangeStepMode = isEditMode && !isChangingStep;

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        height: 100%;
      `}
    >
      <div
        css={css`
          display: flex;
          flex-direction: column;
          gap: ${theme.spacing(3)};
          height: 100%;
          overflow-y: scroll;
          overflow-x: hidden;
          padding: 32px 32px 0 32px;
          margin: 4px;

          ::-webkit-scrollbar {
            -webkit-appearance: none;
            width: 7px;
          }

          ::-webkit-scrollbar-thumb {
            border-radius: 4px;
            background-color: rgba(0, 0, 0, 0.5);
            box-shadow: 0 0 1px rgba(255, 255, 255, 0.5);
          }
        `}
      >
        <HeaderBar title={title} description={description} onClose={onClose} />

        {children}
      </div>

      <ButtonBar
        primaryButtonLabel={primaryButtonLabel ? primaryButtonLabel : t('Done')}
        primaryButtonDisabled={primaryButtonDisabled}
        backButtonLabel={isChangeStepMode ? t('Change Step') : t('Back')}
        omitBackButtonIcon={omitBackActions || isChangeStepMode}
        onPrimaryButtonClick={onPrimaryButtonClick}
        onCancelClick={onCancelClick}
        onBackClick={omitBackActions ? undefined : onBackClick}
        trackingIdPrefix={trackingIdPrefix}
      />
    </div>
  );
};
